<script setup>
    import { ref } from 'vue';
    import { watchDebounced } from '@vueuse/core';
    import { useSitesStore } from '@/stores/sites';
    import { useEventsStore } from '@/stores/events';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';

    const frontEnd = useRuntimeConfig().public.clientFrontend;
    const route = useRoute();

    // Events Store
    const { eventsData } = storeToRefs(useEventsStore());
    const events = computed(() => eventsData.value?.records);

    // Sites Store to get Event Site
    const { sitesData } = storeToRefs(useSitesStore());

    // Data
    const mapView = ref(false);
    const showFilters = ref(false);

    // Filters/Sorts
    const searchTerm = ref('');

    const eventsSource = ref([]);

    const loading = ref(true);

    function formatEvents() {
        if (events.value && events.value.length > 0) {
            let items = [...events.value];
            items.forEach((el) => {
                const site = sitesData.value.find((site) => site.id === el?.fields?.Site?.[0]);
                // Only set Geocoordinates if the site exists and has valid coordinates
                if (site?.fields?.Geocoordinates) {
                    el.fields.Geocoordinates = site.fields.Geocoordinates;
                } else {
                    // Set a default or skip this event for map display
                    el.fields.Geocoordinates = null;
                }
            });
            eventsSource.value = items;
            loading.value = false;
        }
    }

    // Deep link from a neighborhood detail page: /events?neighborhood=<id>.
    // Events carry no neighborhood themselves — they point at a Site, and the
    // site carries `OHC Neighborhood` — so resolve through the sites store.
    const { neighborhoodsData } = storeToRefs(useNeighborhoodsStore());

    // Filter state for the redesigned list (Asana 1216825740083498).
    // `neighborhood` is seeded from ?neighborhood= so the deep link from a
    // neighborhood page still lands pre-filtered, and now shows in the dropdown
    // instead of a separate chip.
    const listFilters = ref({
        // A list, exactly like `times`: no selection and every selection both
        // mean "don't filter by day". That is what removed the Both option
        // (Asana O2-30) — see events-panel.vue.
        days: [],
        times: [],
        neighborhood: route.query.neighborhood || null,
    });

    // filter_applied for the events page.
    //
    // Instrumented here rather than in the two filter components because there
    // are two routes into the same state — the inline pills and select above
    // $tablet (BlocksEventsFilters), and the flyout's Apply below it
    // (FiltersEventsPanel) — and both commit by emitting a replacement object
    // through this v-model. One watcher on the committed state cannot drift
    // from the controls the way two copies of the tracking would.
    //
    // This replaces the tracking in components/Filters/events.vue, which has
    // been dead since 299d195c rebuilt this page and stopped rendering it — the
    // events half of the taxonomy has been silent since 2026-08-14.
    //
    // Only categories that CHANGED and are now non-default are reported, which
    // is the rule Filters/sites.vue already applies on Apply, so the two pages
    // produce comparable data. Clearing a filter is therefore not an event.
    // The watcher is intentionally not deep: both components replace the whole
    // object, so `prev` is the genuinely previous value, and the ?neighborhood=
    // seed above happens at ref creation and so never fires.
    watch(listFilters, (next, prev) => {
        const { track } = useAnalytics();
        const isDefault = (value) => (Array.isArray(value) ? value.length === 0 : value === null || value === undefined);
        const changed = (a, b) => JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);

        for (const filter_type of ['days', 'times', 'neighborhood']) {
            const value = next[filter_type];
            if (!changed(value, prev?.[filter_type]) || isDefault(value)) continue;
            track('filter_applied', { filter_type, value: JSON.stringify(value) });
        }
    });

    // Airtable serialises an event's LOCAL start time with a `Z` on the end:
    // "2025-10-19T14:00:00.000Z" is the 2 PM slot, and its own `Time` field reads
    // "2 PM - 3 PM". The instant it denotes is therefore five hours earlier than
    // the event actually starts, so converting it to America/Chicago — the
    // obviously-correct-looking thing to do — moved every event back into the
    // morning and made the Morning filter match nearly the whole day (O2-34).
    //
    // Reading the UTC components gives the wall-clock time Airtable meant. Same
    // reasoning for the date: converting shifts anything before 5 AM onto the
    // previous day.
    const eventDate = (event) => {
        const iso = event?.fields?.['Date'];
        if (!iso) return null;
        const date = new Date(iso);
        return Number.isNaN(date.getTime()) ? null : date;
    };
    const eventDayKey = (date) => `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;

    // Days come from the data rather than being hardcoded, so the labels stay
    // right when the festival dates move year to year.
    const dayOptions = computed(() => {
        const seen = new Map();
        for (const event of eventsSource.value || []) {
            const date = eventDate(event);
            if (!date) continue;
            const key = eventDayKey(date);
            if (seen.has(key)) continue;
            seen.set(key, {
                value: key,
                label: date.toLocaleDateString('en-US', {
                    timeZone: 'UTC',
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                }),
                sort: date.getTime(),
            });
        }
        return [...seen.values()].sort((a, b) => a.sort - b.sort);
    });

    // Only neighborhoods that actually host an event, so the dropdown never
    // offers a choice that returns nothing.
    const neighborhoodOptions = computed(() => {
        const ids = new Set();
        for (const event of eventsSource.value || []) {
            const site = sitesData.value?.find((s) => s.id === event?.fields?.Site?.[0]);
            const id = site?.fields?.['OHC Neighborhood']?.[0];
            if (id) ids.add(id);
        }
        return (neighborhoodsData.value || [])
            .filter((n) => ids.has(n.id))
            // `Region` does not exist in Airtable yet (Asana O2-32) — until CAC
            // adds it and it is added to the collection's `fields`, this is
            // undefined and the placeholder map in useNeighborhoodGroups applies.
            .map((n) => ({ id: n.id, name: n.fields?.Name || '', region: n.fields?.['Neighborhood Groupings'] || null }))
            .sort((a, b) => a.name.localeCompare(b.name));
    });

    const neighborhoodFilterId = computed(() => listFilters.value.neighborhood);

    const neighborhoodSiteIds = computed(() => {
        const ids = new Set();
        if (!neighborhoodFilterId.value) return ids;
        for (const site of sitesData.value || []) {
            if (site?.fields?.['OHC Neighborhood']?.[0] === neighborhoodFilterId.value) ids.add(site.id);
        }
        return ids;
    });

    // "Morning" is anything starting before noon; "Afternoon" is noon onwards.
    const startsIn = (event, slot) => {
        const date = eventDate(event);
        if (!date) return false;
        return slot === 'morning' ? date.getUTCHours() < 12 : date.getUTCHours() >= 12;
    };

    const filteredEvents = computed(() => {
        let filtered = eventsSource.value;
        // Applied here rather than inside eventsSource so the filter flyout's
        // updateEvents/resetEvents emits can't silently drop them.
        if (neighborhoodFilterId.value) {
            const ids = neighborhoodSiteIds.value;
            filtered = filtered.filter((el) => (el?.fields?.Site || []).some((siteId) => ids.has(siteId)));
        }
        if (listFilters.value.days.length) {
            filtered = filtered.filter((el) => {
                const date = eventDate(el);
                if (!date) return false;
                return listFilters.value.days.includes(eventDayKey(date));
            });
        }
        if (listFilters.value.times.length) {
            filtered = filtered.filter((el) => listFilters.value.times.some((slot) => startsIn(el, slot)));
        }
        if (searchTerm.value) {
            filtered = filtered.filter((el) => {
                return el.fields.Name.toLowerCase().includes(searchTerm.value.toLowerCase());
            });
        }
        return filtered;
    });

    // Search analytics: debounced so we don't emit an event per keystroke.
    watchDebounced(
        searchTerm,
        (val) => {
            if (val && val.length) {
                const { track } = useAnalytics();
                track('search_performed', { query_length: val.length, results_count: filteredEvents.value.length });
            }
        },
        { debounce: 500 }
    );

    // Separate computed for events with valid coordinates for the map
    const mapEvents = computed(() => {
        return filteredEvents.value.filter(event => {
            const coords = event?.fields?.Geocoordinates;
            if (!coords) return false;
            
            // Check if coordinates are valid
            const parts = coords.split(',');
            if (parts.length !== 2) return false;
            
            const lat = parseFloat(parts[0].trim());
            const lng = parseFloat(parts[1].trim());
            
            return !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
        });
    });

    function toggleFilters() {
        searchTerm.value = '';
        showFilters.value = !showFilters.value;
    }

    function viewToggle(n) {
        return (mapView.value = n);
    }

    watch(events, () => {
        formatEvents();
    });

    // Built during setup rather than in onMounted: the events are already in
    // the store when the server renders, and waiting for mount meant the
    // server HTML had no list at all — no h1 and not one <a href="/events/…">
    // for a crawler to follow. Setup runs on the client too, so hydration sees
    // the same markup.
    formatEvents();

</script>

<template>
    <section class="events-overview" :class="{ 'is-map-view': mapView }" v-if="!loading && events && events.length > 0">
        <!-- Same flyout the Sites and City Tours pages use. Day stays inline as
             pills, since the redesign treats it as the primary control; time of
             day and neighborhood live in here. -->
        <GlobalFlyout v-show="showFilters" @toggleFilters="toggleFilters" :showing="showFilters">
            <FiltersEventsPanel
                v-model="listFilters"
                :days="dayOptions"
                :neighborhoods="neighborhoodOptions"
                @close="toggleFilters"
            />
        </GlobalFlyout>

        <div id="content-wrapper">
            <BlocksTitleMapListToggle @viewToggle="viewToggle" title="Events" v-show="!showFilters" :mapView="mapView">
                <button v-if="!showFilters" class="show-filters" @click="toggleFilters" ref="filterToggle" aria-label="Filters">
                    <SvgsFilters class="filter-icon" />
                </button>
                <button v-if="!showFilters" class="desktop-filters button black" @click="toggleFilters" ref="filterToggle">Filters</button>
            </BlocksTitleMapListToggle>

            <BlocksEventsFilters
                v-show="!showFilters && !mapView"
                v-model="listFilters"
                :days="dayOptions"
                :neighborhoods="neighborhoodOptions"
                :count="filteredEvents.length"
            />

            <div class="container hd-container"  v-show="!showFilters && !mapView">
                <div class="filters" role="search">
                    <div class="form-field search">
                        <label for="events-search" v-show="searchTerm === ''">Search events</label>
                        <SvgsSearch />
                        <!-- aria-label as well as the label: the label is hidden once
                             there's text, so it can't be relied on for the name. -->
                        <input id="events-search" type="search" aria-label="Search events" v-model="searchTerm" />
                    </div>
                </div>
            </div>

            <BlocksMap v-show="mapView && !showFilters" :locations="mapEvents" content="event" :subtractHeight="58 + 72" />
            <BlocksEventsRows v-show="!mapView && !showFilters" :items="filteredEvents" />

            <div class="container hd-container" v-if="(mapView && mapEvents.length === 0) || (!mapView && filteredEvents.length === 0)">
                <template v-if="!mapView">
                    <p class="no-results subhead-2">No events match these filters. Try removing a filter.</p>
                </template>
                <template v-else>
                    <BlocksTurquoiseNoResults type="events" />
                </template>
            </div>
        </div>
    </section>
</template>

<style lang="scss">
    // The ?neighborhood= deep link now surfaces in the filter bar's dropdown
    // rather than a separate chip, so that chip's styles are gone.
    .events-overview {
        position: relative;
        
        .is-iframe & {
            margin-bottom: 32px;
        }
        
        &.page {
            padding-bottom: 0;
            @media (min-width: $tablet) {
                padding-bottom: calc($bottom + env(safe-area-inset-bottom));
            }
        }

        .title-map-list-toggle.component-header {
            padding: 20px 16px;

            // See Templates/Sites/page.vue — $hd + the two gutters, or the
            // title sits 5px from the window edge between 1150 and 1182.
            @media (min-width: $hd + 32px) {
                padding: 20px 0px;
            }
        }        

        .filters {
            display: flex;
            margin-bottom: 18px;

            .filter-icon {
                flex-shrink: 0;
                margin-right: 16px;
            }

            .select {
                label {
                    display: block;
                    margin-bottom: 16px;
                }
            }
        }

        .map-sites-carousel {
            .component-header {
                display: none;
            }
            .carousel-component .carousel-inner {
                padding-top: 4px;
                padding-bottom: 4px;
            }
        }

        .desktop-filters {
            display: none;
        }

        .carousel-component .carousel-inner .carousel-item {
            align-self: flex-end;
        }

        @media (min-width: $tablet) {
            width: 100vw;
            max-width: $hd;
            margin: auto;

            .filters {
                width: 100%;
            }

            .show-filters {
                display: none;
            }

            // Stays hidden here too. Time and neighborhood now sit inline in
            // the filter bar from this width up, so the button opened a flyout
            // holding nothing the page was not already showing (Asana O2-21).
            // The icon below $tablet is untouched, which is the split Christine
            // asked for; Sites keeps its own button either way.
            .desktop-filters {
                display: none;
            }

            // Except in map view, which hides the inline bar — without this
            // there is no way to reach the filters at all on desktop. The icon
            // rather than the Filters button, so the map's header keeps to the
            // two compact controls it already has.
            &.is-map-view .show-filters {
                display: block;
            }

            .carousel-component .carousel-inner .carousel-item {
                align-self: stretch;
            }
        }
    }
</style>
